import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import CustomModal from '../customModal';
import VirtualizedList from '../virtualizedList';
import Reviewer from './reviewer';
import { isArrayEmpty } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  description: {
    marginBottom: spacing(4),
  },
  descriptionWithoutNotification: {
    marginTop: spacing(6),
  },
  usersViewport: {
    maxHeight: 147,
  },
  userWrapper: {
    borderBottom: `1px solid ${primary.bluish7}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  scrollY: {
    right: -19,
  },
}));

const SurveyActionsDialog = ({
  translations,
  isOpened,
  usersToNotify = [],
  usersWithoutNotification = [],
  onClose,
  onConfirm,
}) => {
  const classes = useStyles();
  const hasUsersToNotify = !isArrayEmpty(usersToNotify);

  const getVirtualReviewerToNotifySize = useCallback(
    index => (index === usersToNotify.length - 1 ? 24 : 32),
    [usersToNotify.length]
  );

  const getVirtualReviewerWithoutNotificationSize = useCallback(
    index => (index === usersWithoutNotification.length - 1 ? 24 : 32),
    [usersWithoutNotification.length]
  );

  const renderUser = useCallback(
    reviewer => <Reviewer reviewer={reviewer} />,
    []
  );

  return (
    <CustomModal
      title={translations.title}
      closeButtonLabel={translations.close}
      confirmButtonLabel={hasUsersToNotify ? translations.confirm : undefined}
      isOpened={isOpened}
      onClose={onClose}
      onConfirm={onConfirm}
      isMedium
    >
      <>
        {hasUsersToNotify && (
          <div>
            <Typography className={classes.description}>
              {translations.description}
            </Typography>
            <div>
              <VirtualizedList
                viewportClass={classes.usersViewport}
                verticalScrollClass={classes.scrollY}
                itemClass={classes.userWrapper}
                items={usersToNotify}
                count={usersToNotify?.length ?? 0}
                gap={8}
                estimateSize={getVirtualReviewerToNotifySize}
                renderItem={renderUser}
              />
            </div>
          </div>
        )}
        {!isArrayEmpty(usersWithoutNotification) && (
          <div>
            <Typography
              className={classNames(classes.description, {
                [classes.descriptionWithoutNotification]: hasUsersToNotify,
              })}
            >
              {translations.withoutNotification}
            </Typography>
            <div>
              <VirtualizedList
                viewportClass={classes.usersViewport}
                verticalScrollClass={classes.scrollY}
                itemClass={classes.userWrapper}
                items={usersWithoutNotification}
                count={usersWithoutNotification?.length ?? 0}
                gap={8}
                estimateSize={getVirtualReviewerWithoutNotificationSize}
                renderItem={renderUser}
              />
            </div>
          </div>
        )}
      </>
    </CustomModal>
  );
};

SurveyActionsDialog.propTypes = {
  translations: PropTypes.object.isRequired,
  isOpened: PropTypes.bool.isRequired,
  usersWithoutNotification: PropTypes.arrayOf(PropTypes.shape({})),
  usersToNotify: PropTypes.arrayOf(PropTypes.shape({})),
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
export default SurveyActionsDialog;
